import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '../../../hooks/useScrollLock'
import { useTranslation } from 'react-i18next'
import type { Model } from '../../../data/models'
import { useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { isMotionCard } from '../motion/motionTool'
import ModelIcon from '../../../components/ModelIcon'
import { AspectRatioIcon } from '../../../components/AspectRatioIcon'
import type { GenerateSettings } from '../hooks/useGenerateSettings'
import { modelParams, outputFormatOptions, effectiveOutputFormat, trimMinSeconds } from '../utils/modelParams'
import {
	ASPECT_FOLLOWS_LABEL,
	NOTE_ASPECT_FOLLOWS,
	DRAFT_HINT,
	DRAFT_RESOLUTION_LABEL,
	NOTE_DRAFT,
	VIDEO_INTENT_HINTS,
	VIDEO_INTENT_OPTIONS,
	aspectFollowsReference,
	durationLock,
	motionDurationForOrientation,
	ownReferenceVideos,
	trendVideoNote,
	type VideoIntent,
	videoIntentOf,
} from '../utils/referenceLimits'
import SunoStyleSheet from './SunoStyleSheet'
import { useVoiceLabel, type TtsVoice } from '../utils/ttsVoices'
import VoiceLibraryPanel from './VoiceLibraryPanel'
import { TRACK_PARTS } from '../utils/trackParts'
import { providerOf } from '../utils/trackOperations'
import Sheet from '../../../components/Sheet'
import GammaThemesGallery from './GammaThemesGallery'
import {
	GAMMA_IMAGE_MODEL_OPTIONS,
	GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE,
	GAMMA_TEXT_MODE_OPTIONS,
} from '../utils/gammaOptions'
import GammaHeaderFooterSheet from './GammaHeaderFooterSheet'
import { hfSettingsPatch, hfStateFromSettings, hfSummaryParts, type GammaHfState } from '../utils/gammaHf'
import type { PresentationSettings } from '../hooks/useGenerateSettings'
import { getHeyGenModelKind } from '../../../api/heygen'
import { qualityLabelFull } from '../utils/qualityLabels'
import {
	HeyGenAvatarMobileRow,
	HeyGenVoiceMobileRow,
	HeyGenTranslateUrlField,
	HEYGEN_AVATAR_STYLES,
	HEYGEN_EMOTIONS,
	HEYGEN_SPEED,
	useHeyGenTranslationLanguages,
} from './HeyGenPanels'

const AUDIO_VOCAL_OPTIONS_KEYS = [
	{ value: '', labelKey: 'generate.audio.vocalAuto' },
	{ value: 'm', labelKey: 'generate.audio.vocalMale' },
	{ value: 'f', labelKey: 'generate.audio.vocalFemale' },
]

interface ModelRowProps {
	current: Model
	models: Model[]
	onSelect: (m: Model) => void
	open: boolean
	onToggle: () => void
	modelLabel: string
}

function ModelRow({ current, models, onSelect, open, onToggle, modelLabel }: ModelRowProps) {
	return (
		<div className={`mss-row mss-row--select mss-row--model ${open ? 'mss-row--open' : ''}`}>
			<button type="button" className="mss-row__header" onClick={onToggle}>
				<div className="mss-row__label-wrap">
					<span className="mss-row__label">{modelLabel}</span>
				</div>
				<div className="mss-row__current">
					<ModelIcon icon={current.icon} name={current.name} size={20} className="mss-model-icon" />
					<span>{current.shortName || current.name}</span>
					<span className={`mss-row__chevron ${open ? 'mss-row__chevron--up' : ''}`}>
						<IconChevron />
					</span>
				</div>
			</button>
			<div className={`mss-options-wrap ${open ? 'mss-options-wrap--open' : ''}`}>
				<div className="mss-options-wrap__inner">
					<div className="mss-options mss-options--models">
						{models.map((m) => (
							<button
								key={m.id}
								className={`mss-option mss-option--model ${m.id === current.id ? 'mss-option--active' : ''}`}
								onClick={() => { onSelect(m); onToggle() }}
							>
								<ModelIcon icon={m.icon} name={m.name} size={20} className="mss-model-icon" />
								<span className="mss-option__label">{m.name}</span>
								{m.id === current.id && (
									<span className="check-badge" />
								)}
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

interface VoiceRowProps {
	currentVoice: string | undefined
	currentVoiceName: string | undefined
	onSelect: (voice: TtsVoice) => void
	open: boolean
	onToggle: () => void
}

/** Голос озвучки в шторке: аккордеон как «Модель», внутри — та же панель
 *  библиотеки (язык, сценарий, голоса), что в окне виджета на десктопе. */
function VoiceRow({ currentVoice, currentVoiceName, onSelect, open, onToggle }: VoiceRowProps) {
	const label = useVoiceLabel(currentVoice, currentVoiceName)
	return (
		<div className={`mss-row mss-row--select mss-row--voice ${open ? 'mss-row--open' : ''}`}>
			<button type="button" className="mss-row__header" onClick={onToggle}>
				<div className="mss-row__label-wrap">
					<span className="mss-row__label">Голос</span>
				</div>
				<div className="mss-row__current">
					<span>{label}</span>
					<span className={`mss-row__chevron ${open ? 'mss-row__chevron--up' : ''}`}>
						<IconChevron />
					</span>
				</div>
			</button>
			<div className={`mss-options-wrap ${open ? 'mss-options-wrap--open' : ''}`}>
				<div className="mss-options-wrap__inner">
					{open && (
						<VoiceLibraryPanel
							currentVoiceId={currentVoice}
							onSelect={(v) => {
								onSelect(v)
								onToggle()
							}}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

const IconClose = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
	</svg>
)
const IconChevron = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
)
const IconInfo = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<circle cx="12" cy="12" r="9" />
		<path d="M12 8v1M12 12v4" strokeLinecap="round" />
	</svg>
)

interface HintBubbleProps {
	text: string
	hintAriaLabel: string
}

function HintBubble({ text, hintAriaLabel }: HintBubbleProps) {
	const [visible, setVisible] = useState(false)
	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!visible) return
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setVisible(false)
			}
		}
		document.addEventListener('mousedown', handleClick, true)
		return () => document.removeEventListener('mousedown', handleClick, true)
	}, [visible])

	return (
		<div ref={ref} className="mss-hint-wrap">
			<span
				role="button"
				tabIndex={0}
				className={`mss-hint-btn ${visible ? 'mss-hint-btn--active' : ''}`}
				onClick={(e) => {
					e.stopPropagation()
					setVisible((v) => !v)
				}}
				onKeyDown={(e) => e.key === 'Enter' && setVisible((v) => !v)}
				aria-label={hintAriaLabel}
			>
				<IconInfo />
			</span>
			{visible && <div className="mss-hint-bubble">{text}</div>}
		</div>
	)
}

interface SettingRowSelectProps {
	label: string
	hint?: string
	hintAriaLabel?: string
	options: { value: string; label: string; icon?: React.ReactNode }[]
	value: string
	onChange: (v: string) => void
	disabled?: boolean
	open: boolean
	onToggle: () => void
}

function SettingRowSelect({
	label,
	hint,
	hintAriaLabel,
	options,
	value,
	onChange,
	disabled,
	open,
	onToggle,
}: SettingRowSelectProps) {
	const current = options.find((o) => o.value === value)
	return (
		<div className={`mss-row mss-row--select ${open ? 'mss-row--open' : ''}`}>
			<button type="button" className="mss-row__header" onClick={onToggle}>
				<div className="mss-row__label-wrap">
					{hint && <HintBubble text={hint} hintAriaLabel={hintAriaLabel || ''} />}
					<span className="mss-row__label">{label}</span>
				</div>
				<div className="mss-row__current">
					{current?.icon && <span className="mss-row__current-icon">{current.icon}</span>}
					<span>{current?.label ?? value}</span>
					<span className={`mss-row__chevron ${open ? 'mss-row__chevron--up' : ''}`}>
						<IconChevron />
					</span>
				</div>
			</button>
			<div className={`mss-options-wrap ${open ? 'mss-options-wrap--open' : ''}`}>
				<div className="mss-options-wrap__inner">
					<div className="mss-options">
						{options.map((opt) => (
							<button
								key={opt.value}
								className={`mss-option ${opt.value === value ? 'mss-option--active' : ''}`}
								onClick={() => {
									if (!disabled) {
										onChange(opt.value)
										onToggle()
									}
								}}
								disabled={disabled}
							>
								{opt.icon && <span className="mss-option__icon">{opt.icon}</span>}
								<span className="mss-option__label">{opt.label}</span>
								{opt.value === value && (
									<span className="check-badge" />
								)}
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

/** Строка без выбора: значение задаёт не человек (тренд переделывает своё видео — shared/trend-video). */
function SettingRowInfo({ label, value }: { label: string; value: string }) {
	return (
		<div className="mss-row mss-row--info">
			<div className="mss-row__header">
				<div className="mss-row__label-wrap">
					<span className="mss-row__label">{label}</span>
				</div>
				<div className="mss-row__current">
					<span>{value}</span>
				</div>
			</div>
		</div>
	)
}

interface SettingRowToggleProps {
	label: string
	hint?: string
	hintAriaLabel?: string
	value: boolean
	onChange: (v: boolean) => void
	disabled?: boolean
}

function SettingRowToggle({ label, hint, hintAriaLabel, value, onChange, disabled }: SettingRowToggleProps) {
	return (
		<div className="mss-row mss-row--toggle">
			<div className="mss-row__label-wrap">
				{hint && <HintBubble text={hint} hintAriaLabel={hintAriaLabel || ''} />}
				<span className="mss-row__label">{label}</span>
			</div>
			<label className="mss-toggle">
				<input
					type="checkbox"
					checked={value}
					onChange={(e) => !disabled && onChange(e.target.checked)}
					disabled={disabled}
				/>
				<span className="mss-toggle__slider" />
			</label>
		</div>
	)
}

interface SettingRowSliderProps {
	label: string
	hint?: string
	hintAriaLabel?: string
	value: number
	min: number
	max: number
	step?: number
	unit?: string
	onChange: (v: number) => void
	disabled?: boolean
}

function SettingRowSlider({
	label,
	hint,
	hintAriaLabel,
	value,
	min,
	max,
	step = 1,
	unit = '',
	onChange,
	disabled,
}: SettingRowSliderProps) {
	return (
		<div className="mss-row mss-row--slider">
			<div className="mss-row__header" style={{ pointerEvents: 'none' }}>
				<div className="mss-row__label-wrap" style={{ pointerEvents: 'auto' }}>
					{hint && <HintBubble text={hint} hintAriaLabel={hintAriaLabel || ''} />}
					<span className="mss-row__label">{label}</span>
				</div>
				<span className="mss-row__current-val">
					{value}
					{unit}
				</span>
			</div>
			<input
				type="range"
				className="mss-slider"
				min={min}
				max={max}
				step={step}
				value={value}
				onChange={(e) => onChange(Number(e.target.value))}
				disabled={disabled}
			/>
			<div className="mss-slider-labels">
				<span>
					{min}
					{unit}
				</span>
				<span>
					{max}
					{unit}
				</span>
			</div>
		</div>
	)
}

// ── Gamma Theme mobile row ──────────────────────────────────────
// Кнопка-row которая открывает Sheet с GammaThemesGallery внутри.
// Аналог Avatar/Voice picker'ов у HeyGen на мобилке.
interface GammaThemeMobileRowProps {
	selectedThemeId: string | undefined
	selectedThemeName?: string
	onSelect: (themeId: string | undefined, themeName?: string) => void
	disabled?: boolean
}

function GammaThemeMobileRow({ selectedThemeId, selectedThemeName, onSelect, disabled }: GammaThemeMobileRowProps) {
	const [open, setOpen] = useState(false)
	const display = selectedThemeId ? (selectedThemeName || selectedThemeId) : 'Авто'

	return (
		<>
			<div className="mss-row mss-row--select">
				<button
					type="button"
					className="mss-row__header"
					onClick={() => !disabled && setOpen(true)}
					disabled={disabled}
				>
					<div className="mss-row__label-wrap">
						<span className="mss-row__label">Тема оформления</span>
					</div>
					<div className="mss-row__current">
						<span className="mss-row__current-val">{display}</span>
						<span className="mss-row__chevron">
							<IconChevron />
						</span>
					</div>
				</button>
			</div>
			<Sheet
				open={open}
				onClose={() => setOpen(false)}
				variant="bottom"
				title="Тема оформления"
				showHandle
				className="gamma-theme-sheet"
			>
				<div className="gamma-theme-sheet__body">
					<GammaThemesGallery
						selectedThemeId={selectedThemeId}
						onSelectTheme={(id, name) => {
							onSelect(id, name)
							setOpen(false)
						}}
						disabled={disabled}
					/>
				</div>
			</Sheet>
		</>
	)
}

// ── Gamma Instructions mobile row ───────────────────────────────
// Inline textarea для дополнительных инструкций. Без отдельного Sheet —
// длинный текст лучше виден сразу в settings sheet.
interface GammaInstructionsMobileRowProps {
	value: string
	onChange: (value: string) => void
	disabled?: boolean
}

function GammaInstructionsMobileRow({
	value,
	onChange,
	disabled,
}: GammaInstructionsMobileRowProps) {
	const MAX_LENGTH = 1000
	return (
		<div className="mss-row mss-row--textarea">
			<div className="mss-row__label-wrap">
				<span className="mss-row__label">Инструкции для AI</span>
			</div>
			<textarea
				className="mss-row__textarea"
				placeholder="Опционально: тон, аудитория, обязательные пункты, чего избегать…"
				value={value}
				onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
				rows={3}
				disabled={disabled}
			/>
			{value && (
				<div className="mss-row__textarea-counter">
					{value.length} / {MAX_LENGTH}
				</div>
			)}
		</div>
	)
}

// ── Gamma Image Style mobile row ────────────────────────────────
// Inline textarea для стиля AI-картинок (imageOptions.style) — тот же
// паттерн, что Instructions. Показывается только при источнике aiGenerated.
function GammaImageStyleMobileRow({
	value,
	onChange,
	disabled,
}: GammaInstructionsMobileRowProps) {
	const MAX_LENGTH = 5000
	return (
		<div className="mss-row mss-row--textarea">
			<div className="mss-row__label-wrap">
				<span className="mss-row__label">Стиль картинок</span>
			</div>
			<textarea
				className="mss-row__textarea"
				placeholder="Например: «акварель, пастельные тона» или «строгий фотореализм, синяя палитра»"
				value={value}
				onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
				rows={2}
				disabled={disabled}
			/>
			{value && (
				<div className="mss-row__textarea-counter">
					{value.length} / {MAX_LENGTH}
				</div>
			)}
		</div>
	)
}

// ── Gamma Header/Footer mobile row ──────────────────────────────
// Строка «Колонтитулы» → общий GammaHeaderFooterSheet снизу (variant bottom,
// как галерея тем). Значение строки: «Текст + №» / «Нет».
interface GammaHeaderFooterMobileRowProps {
	pres: PresentationSettings
	onSave: (v: GammaHfState) => void
	disabled?: boolean
}

function GammaHeaderFooterMobileRow({ pres, onSave, disabled }: GammaHeaderFooterMobileRowProps) {
	const [open, setOpen] = useState(false)
	const parts = hfSummaryParts(pres)
	return (
		<>
			<div className="mss-row mss-row--select">
				<button
					type="button"
					className="mss-row__header"
					onClick={() => !disabled && setOpen(true)}
					disabled={disabled}
				>
					<div className="mss-row__label-wrap">
						<span className="mss-row__label">Колонтитулы</span>
					</div>
					<div className="mss-row__current">
						<span className="mss-row__current-val">{parts.length ? parts.join(' + ') : 'Нет'}</span>
						<span className="mss-row__chevron">
							<IconChevron />
						</span>
					</div>
				</button>
			</div>
			{open && (
				<GammaHeaderFooterSheet
					open={open}
					variant="bottom"
					value={hfStateFromSettings(pres)}
					onSave={onSave}
					onClose={() => setOpen(false)}
				/>
			)}
		</>
	)
}

// ── Gamma Audience mobile row ───────────────────────────────────
// Короткое поле single-line. Целевая аудитория обычно одной фразой.
interface GammaAudienceMobileRowProps {
	value: string
	onChange: (value: string) => void
	disabled?: boolean
}

function GammaAudienceMobileRow({ value, onChange, disabled }: GammaAudienceMobileRowProps) {
	const MAX_LENGTH = 200
	return (
		<div className="mss-row mss-row--textarea">
			<div className="mss-row__label-wrap">
				<span className="mss-row__label">Целевая аудитория</span>
			</div>
			<input
				type="text"
				className="mss-row__textarea"
				style={{ minHeight: 'auto', height: 44 }}
				placeholder="Например: «инвесторы B2B», «школьники 5-7 класс»"
				value={value}
				onChange={(e) => onChange(e.target.value.slice(0, MAX_LENGTH))}
				disabled={disabled}
			/>
		</div>
	)
}

// ── HeyGen video-translate mobile rows ──────────────────────────
// Внутренний helper-компонент, использует SettingRowSelect/Toggle из этого
// же файла + загружает список target-языков из /api/v1/heygen/translation-languages.
interface HeyGenTranslateMobileRowsProps {
	settings: GenerateSettings
	onSettingChange: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	openKey: string | null
	toggle: (key: string) => void
	isLoading: boolean
}

function HeyGenTranslateMobileRows({
	settings,
	onSettingChange,
	openKey,
	toggle,
	isLoading,
}: HeyGenTranslateMobileRowsProps) {
	const languages = useHeyGenTranslationLanguages()

	return (
		<>
			{/* URL input — mobile-friendly inline (тот же компонент что в desktop) */}
			<HeyGenTranslateUrlField
				settings={settings}
				updateSetting={onSettingChange}
				disabled={isLoading}
			/>
			<SettingRowSelect
				label="Язык перевода"
				hint="Целевой язык для перевода видео"
				open={openKey === 'heygenLang'}
				onToggle={() => toggle('heygenLang')}
				options={[
					{ value: '', label: '— Не выбрано —' },
					...languages.map((l) => ({ value: l.code, label: l.label })),
				]}
				value={settings.heygenOutputLanguage || ''}
				onChange={(v) => onSettingChange('heygenOutputLanguage', v || undefined)}
				disabled={isLoading}
			/>
			<SettingRowToggle
				label="Подгонять длительность"
				hint="Подгоняет темп речи под длительность исходного видео"
				value={!!settings.heygenEnableDynamicDuration}
				onChange={(v) => onSettingChange('heygenEnableDynamicDuration', v)}
				disabled={isLoading}
			/>
			<SettingRowToggle
				label="Только аудио"
				hint="Без lipsync — только переводит звуковую дорожку"
				value={!!settings.heygenTranslateAudioOnly}
				onChange={(v) => onSettingChange('heygenTranslateAudioOnly', v)}
				disabled={isLoading}
			/>
		</>
	)
}

interface Props {
	open: boolean
	onClose: () => void
	model: Model
	settings: GenerateSettings
	isLoading: boolean
	selectedVideoDuration: number
	uploadedVideoDuration?: number
	referenceVideoUrl?: string
	negativePromptOpen: boolean
	onSettingChange: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	onVideoDurationChange: (v: number) => void
	onNegativePromptToggle: () => void
	availableModels?: Model[]
	onSelectModel?: (m: Model) => void
	/** Текущая операция над треком — от неё зависят операционные поля
	 *  (например выбор партии для «+ Инструментал»). */
	audioOperation?: string
	/** Модель исходного трека: партии умеет только Mureka. */
	sourceTrackModel?: string | null
	/** Фото разложены по именованным слотам карточки — режим Seedance задан ею. */
	photoSlotsEngaged?: boolean
	/** Смена режима Seedance чистит несовместимые загрузки, поэтому приходит
	 *  готовым обработчиком — та же логика, что у пилюль десктопа. */
	onSeedanceModeChange?: (mode: 'frames' | 'references') => void
	/** Видео-референсы (свои и карточки): от них зависят намерение и замки длительности/формата
	 *  Seedance 2.5 — те же правила, что у пилюль десктопа (utils/referenceLimits). */
	referenceVideos?: { duration?: number; url?: string; cardRef?: boolean }[]
	/** Приложен хоть один файл — спека прячет web-поиск Seedance (работает только без входов). */
	inputsAttached?: boolean
	/** Черновик Seedance 2.5 доступен (GenerateContext.draftAvailable): сервер отдаёт, нет карточки и видео/аудио-референса. */
	draftAvailable?: boolean
	/** Смена намерения подтягивает длительность к референсу — общий обработчик (utils/videoIntent). */
	onVideoIntentChange?: (intent: VideoIntent) => void
}

export default function MobileSettingsSheet({
	open,
	onClose,
	model,
	settings,
	isLoading,
	selectedVideoDuration,
	uploadedVideoDuration,
	referenceVideoUrl,
	negativePromptOpen,
	onSettingChange,
	onVideoDurationChange,
	onNegativePromptToggle,
	availableModels,
	onSelectModel,
	audioOperation,
	sourceTrackModel,
	photoSlotsEngaged,
	onSeedanceModeChange,
	referenceVideos,
	inputsAttached,
	draftAvailable,
	onVideoIntentChange,
}: Props) {
	const { t } = useTranslation()
	const sheetRef = useRef<HTMLDivElement>(null)
	const [openKey, setOpenKey] = useState<string | null>(null)
	const [styleSheetOpen, setStyleSheetOpen] = useState(false)

	const toggle = (key: string) => setOpenKey((prev) => (prev === key ? null : key))

	// Лок скролла страницы под шитом. Через общий хук (лочит html, сохраняет
	// scrollTop) — прежний body{overflow:hidden} схлопывал body и прыгал наверх.
	useScrollLock(open)

	function handleBackdropClick(e: React.MouseEvent) {
		if (e.target === e.currentTarget) onClose()
	}

	const isPresentation = model.category === 'presentation'

	const pres: PresentationSettings = settings.presentation ?? { mode: 'edit' }
	const updatePres = (patch: Partial<PresentationSettings> & { _openImages?: boolean }) =>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onSettingChange('presentation', { ...pres, ...patch } as any)
	// ЧТО показывать решает общий источник (utils/modelParams) — тот же, что у
	// пилюль десктопа. Здесь остаётся только «как»: строки вместо пилюль.
	const p = modelParams(model, {
		referenceVideoUrl,
		photoSlotsEngaged,
		seedanceMode: settings.seedanceMode,
		ownReferenceVideos: ownReferenceVideos(referenceVideos).length,
		inputsAttached,
		draftAvailable,
	})
	const videoMin = trimMinSeconds(p)
	// Черновик включён: разрешение одно (480p) — строка это показывает и заперта, как пилюля на десктопе.
	const draftOn = p.draft && settings.draft === true
	// Замки при видео-референсе Seedance 2.5 — одни правила с пилюлями десктопа
	// (раньше телефон длительность не запирал и обещал юзеру не то, что спишется).
	// Тренд с замком длительности — та же запертая строка с длиной тренда (карточка тренда в админке, 11.10).
	const durLock = durationLock(model, settings, referenceVideos ?? [], !!uploadedVideoDuration)
	const followSec = durLock?.sec ?? null
	const aspectLocked = aspectFollowsReference(model, settings, referenceVideos ?? [])
	// Тренд переделывает своё видео (shared/trend-video): строки формата и длительности заменяет одна — как у десктопа.
	const trendNote = trendVideoNote(model, settings, referenceVideos ?? [])
	const videoIntent = videoIntentOf(settings)
	// Списки значений: показывать их решает спека, а тип у Model опциональный.
	const aspectRatios = model.aspectRatios ?? []
	const durations = model.durations ?? []
	const resolutions = model.resolutions ?? []
	const qualityLevels = model.qualityLevels ?? []
	// «Повторить движение»: звук всегда включён (владелец 19.10) — useMotionMode держит его, переключателей не рисуем.
	const motionTool = isMotionCard(useAttachedStyleCard())
	const showSound = (p.sound || p.generateAudio) && !motionTool
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const paramHints = (model as Record<string, any>).paramHints || {}

	const DEFAULT_HINTS: Record<string, string> = {
		aspectRatio: 'Соотношение сторон итогового медиафайла.',
		duration: 'Длительность генерируемого видео в секундах.',
		resolution: 'Качество и разрешение итогового файла.',
		quality: 'Качество генерации: выше — детальнее, но дольше и дороже; ниже — быстрее и дешевле.',
		sound: 'Автоматическая генерация звукового сопровождения.',
		negativePrompt: 'Что не должно появляться в результате.',
		promptOptimizer: 'Модель автоматически улучшает ваш промт.',
		speed: 'Скорость генерации: Turbo — быстрее, Relaxed — экономичнее.',
		stylization: 'Насколько сильно модель применяет свой художественный стиль.',
		characterOrientation:
			'«По изображению» — сохраняет позу (макс. 10 сек). «По видео» — повторяет ориентацию из видео.',
		keepOriginalSound: 'Сохранить оригинальный звук из загруженного видео.',
	}

	const hint = (key: string) => paramHints[key]?.description || DEFAULT_HINTS[key]
	const hintLabel = (key: string, fallback: string) => paramHints[key]?.label || fallback

	const audioVocalOptions = AUDIO_VOCAL_OPTIONS_KEYS.map((o) => ({ value: o.value, label: t(o.labelKey) }))

	const speedOptions = [
		{ value: 'turbo', label: 'Turbo' },
		{ value: 'fast', label: 'Fast' },
		{ value: 'relaxed', label: 'Relaxed' },
	]
	const versionOptions = [
		{ value: '7', label: 'v7' },
		{ value: '6.1', label: 'v6.1' },
		{ value: '6', label: 'v6' },
		{ value: '5.2', label: 'v5.2' },
		{ value: 'niji6', label: 'Niji 6' },
	]
	const safetyOptions = [
		{ value: 'block_only_high', label: t('generate.settings.safety.minimal') },
		{ value: 'block_medium_and_above', label: t('generate.settings.safety.medium') },
		{ value: 'block_low_and_above', label: t('generate.settings.safety.strict') },
	]
	const formatOptions = outputFormatOptions(model)
	const orientationOptions = [
		{ value: 'video', label: t('generate.settings.orientation.byVideo') },
		{ value: 'image', label: t('generate.settings.orientation.byImage') },
	]

	return createPortal(
		<div
			className={`mss-backdrop ${open ? 'mss-backdrop--open' : ''}`}
			onClick={handleBackdropClick}
		>
			<div
				ref={sheetRef}
				className={`mss-sheet ${open ? 'mss-sheet--open' : ''}`}
				role="dialog"
				aria-modal="true"
				aria-label={t('generate.settings.title')}
			>
				<div className="mss-handle" />

				<div className="mss-header">
					<h2 className="mss-title">{t('generate.settings.title')}</h2>
					<button className="mss-close" onClick={onClose} aria-label={t('common.close')}>
						<IconClose />
					</button>
				</div>

				<div className="mss-content">
					{availableModels && availableModels.length > 0 && onSelectModel && (
						<ModelRow
							current={model}
							models={availableModels}
							onSelect={onSelectModel}
							open={openKey === '__model__'}
							onToggle={() => toggle('__model__')}
							modelLabel={t('generate.settings.model')}
						/>
					)}

					{model.category === 'audio' && settings.audioSubmode !== 'tts' && (
						<>
							{/* Строка-кнопка «Стиль: Pop ›» в стиле других настроек.
							    Клик открывает SunoStyleSheet поверх settings-sheet —
							    отдельная шторка с фокусом только на стиле.
							    `--audio-style` modifier даёт truncation для длинных
							    значений после boost-style (иначе растягивает row'у). */}
							<div className="mss-row mss-row--select mss-row--audio-style">
								<button
									type="button"
									className="mss-row__header"
									onClick={() => setStyleSheetOpen(true)}
									disabled={isLoading}
								>
									<div className="mss-row__label-wrap">
										<span className="mss-row__label">
											{t('generate.settings.style')}
										</span>
									</div>
									<div className="mss-row__current">
										<span className="mss-row__current-val">
											{settings.audioStyle?.trim() || 'Pop'}
										</span>
										<span className="mss-row__chevron">
											<IconChevron />
										</span>
									</div>
								</button>
							</div>
							{/* «Партия» — что дописать к треку. Только Mureka и только
							    в «+ Инструментал»: у Suno эта операция всегда даёт
							    музыку целиком. Справочник общий с десктопом. */}
							{audioOperation === 'add_instrumental'
								&& providerOf(sourceTrackModel) === 'mureka' && (
								<SettingRowSelect
									label="Партия"
									open={openKey === 'audioTrackType'}
									onToggle={() => toggle('audioTrackType')}
									options={TRACK_PARTS.map((p) => ({ value: p.value, label: p.label }))}
									value={settings.audioTrackType || ''}
									onChange={(v) => onSettingChange('audioTrackType', v)}
									disabled={isLoading}
								/>
							)}
							{!settings.audioInstrumental && (
								<SettingRowSelect
									label={t('generate.settings.vocal')}
									open={openKey === 'audioVocal'}
									onToggle={() => toggle('audioVocal')}
									options={audioVocalOptions}
									value={settings.audioVocalGender || ''}
									onChange={(v) => onSettingChange('audioVocalGender', v)}
									disabled={isLoading}
								/>
							)}
							<SettingRowToggle
								label={t('generate.settings.instrumental')}
								value={!!settings.audioInstrumental}
								onChange={(v) => onSettingChange('audioInstrumental', v)}
								disabled={isLoading}
							/>
							<SettingRowToggle
								label={t('generate.settings.customMode')}
								value={settings.audioCustomMode ?? true}
								onChange={(v) => onSettingChange('audioCustomMode', v)}
								disabled={isLoading}
							/>
						</>
					)}

					{/* Озвучка (TTS): выбор голоса — аккордеон-строка как «Модель». */}
					{model.category === 'audio' && settings.audioSubmode === 'tts' && (
						<VoiceRow
							currentVoice={settings.audioVoice}
							currentVoiceName={settings.audioVoiceName}
							onSelect={(v) => {
								onSettingChange('audioVoice', v.id)
								onSettingChange('audioVoiceName', v.label)
							}}
							open={openKey === 'ttsVoice'}
							onToggle={() => toggle('ttsVoice')}
						/>
					)}

					{isPresentation && pres.mode === 'create' && (
						<>
							{/* Тема — кнопка-row, открывает Sheet с GammaThemesGallery */}
							<GammaThemeMobileRow
								selectedThemeId={pres.gammaThemeId}
								selectedThemeName={pres.gammaThemeName}
								onSelect={(themeId, themeName) => updatePres({ gammaThemeId: themeId, gammaThemeName: themeName })}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Слайдов"
								hint="Сколько слайдов сгенерировать. Больше слайдов — подробнее раскрыта тема, но дольше генерация и больше токенов уйдёт."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaNumCards'}
								onToggle={() => toggle('gammaNumCards')}
								options={[
									{ value: '5', label: '5 слайдов' },
									{ value: '8', label: '8 слайдов' },
									{ value: '10', label: '10 слайдов' },
									{ value: '15', label: '15 слайдов' },
									{ value: '20', label: '20 слайдов' },
									{ value: '30', label: '30 слайдов' },
								]}
								value={String(pres.gammaNumCards ?? 5)}
								onChange={(v) => {
									const n = parseInt(v, 10)
									if (Number.isFinite(n) && n > 0) updatePres({ gammaNumCards: n })
								}}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Язык"
								hint="Язык на котором Gamma напишет текст слайдов. Если промпт на русском, а здесь English — содержание переведётся на английский."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaLanguage'}
								onToggle={() => toggle('gammaLanguage')}
								options={[
									{ value: 'ru', label: 'Русский' },
									{ value: 'en', label: 'English' },
									{ value: 'de', label: 'Deutsch' },
									{ value: 'fr', label: 'Français' },
									{ value: 'es', label: 'Español' },
									{ value: 'zh', label: '中文' },
								]}
								value={pres.gammaLanguage ?? 'ru'}
								onChange={(v) => updatePres({ gammaLanguage: v })}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Режим текста"
								hint="«Сгенерировать» — промпт это тема. «Сжать мой текст» — длинный текст сожмётся в слайды. «Как есть» — текст ляжет на слайды без переписывания."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaTextMode'}
								onToggle={() => toggle('gammaTextMode')}
								options={GAMMA_TEXT_MODE_OPTIONS}
								value={pres.gammaTextMode ?? 'generate'}
								onChange={(v) => updatePres({ gammaTextMode: v as PresentationSettings['gammaTextMode'] })}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Изображения"
								hint="Откуда брать иллюстрации для слайдов: AI-генерация (уникальные), Pexels (стоковые фото), Giphy (анимация), иконки, заглушки или вовсе без картинок."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaImageSource'}
								onToggle={() => toggle('gammaImageSource')}
								options={[
									{ value: 'aiGenerated', label: 'AI-генерация' },
									{ value: 'webAllImages', label: 'Из интернета' },
									{ value: 'webFreeToUse', label: 'Бесплатные фото' },
									{ value: 'pexels', label: 'Pexels' },
									{ value: 'giphy', label: 'GIF (Giphy)' },
									{ value: 'pictographic', label: 'Иконки' },
									{ value: 'themeAccent', label: 'Декор темы' },
									{ value: 'placeholder', label: 'Заглушки' },
									{ value: 'noImages', label: 'Без картинок' },
								]}
								value={pres.gammaImageSource ?? 'aiGenerated'}
								onChange={(v) =>
									updatePres({ gammaImageSource: v as PresentationSettings['gammaImageSource'] })
								}
								disabled={isLoading}
							/>
							{(pres.gammaImageSource ?? 'aiGenerated') === 'aiGenerated' && (
								<SettingRowSelect
									label="Модель картинок"
									hint="Какая нейросеть рисует картинки слайдов. Премиум-модели качественнее, но дороже — надбавка за каждый слайд."
									hintAriaLabel={t('generate.settings.hint')}
									open={openKey === 'gammaImageModel'}
									onToggle={() => toggle('gammaImageModel')}
									options={GAMMA_IMAGE_MODEL_OPTIONS.map((o) => {
										const sur = GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE[o.tier] || 0
										return {
											value: o.value,
											label: sur > 0 ? `${o.label} (+${sur}✦/слайд)` : o.label,
											icon: (
												<ModelIcon icon={o.iconUrl} name={o.label} size={20} className="gamma-model-logo" />
											),
										}
									})}
									value={pres.gammaImageModel ?? ''}
									onChange={(v) => updatePres({ gammaImageModel: v || undefined })}
									disabled={isLoading}
								/>
							)}
							{(pres.gammaImageSource ?? 'aiGenerated') === 'aiGenerated' && (
								<GammaImageStyleMobileRow
									value={pres.gammaImageStyle || ''}
									onChange={(text) => updatePres({ gammaImageStyle: text || undefined })}
									disabled={isLoading}
								/>
							)}
							<SettingRowSelect
								label="Формат слайда"
								hint="Соотношение сторон. 16:9 — современный широкоформатный (для экранов), 4:3 — классический (печать, A4, старые проекторы)."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaDimensions'}
								onToggle={() => toggle('gammaDimensions')}
								options={[
									{ value: '16x9', label: '16:9 (горизонтальный)' },
									{ value: '4x3', label: '4:3 (классический)' },
									{ value: '1x1', label: '1:1 (квадрат — посты)' },
									{ value: '4x5', label: '4:5 (портрет — лента)' },
									{ value: '9x16', label: '9:16 (вертикальный — сторис)' },
								]}
								value={pres.gammaDimensions ?? '16x9'}
								onChange={(v) => updatePres({ gammaDimensions: v as PresentationSettings['gammaDimensions'] })}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Объём текста"
								hint="Сколько текста размещать на одном слайде. Кратко — bullet-points (для презентации с речью), Средне — баланс, Подробно — развёрнутые абзацы (для самостоятельного чтения)."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaTextAmount'}
								onToggle={() => toggle('gammaTextAmount')}
								options={[
									{ value: 'brief', label: 'Кратко' },
									{ value: 'medium', label: 'Средне' },
									{ value: 'detailed', label: 'Подробно' },
									{ value: 'extensive', label: 'Максимум' },
								]}
								value={pres.gammaTextAmount ?? 'medium'}
								onChange={(v) =>
									updatePres({ gammaTextAmount: v as PresentationSettings['gammaTextAmount'] })
								}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Файл"
								hint="Формат готового файла. PPTX — правится в PowerPoint/Keynote, PDF — для отправки и печати, PNG — картинки слайдов."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaExportAs'}
								onToggle={() => toggle('gammaExportAs')}
								options={[
									{ value: 'pptx', label: 'PPTX (правится в PowerPoint)' },
									{ value: 'pdf', label: 'PDF (для отправки и печати)' },
									{ value: 'png', label: 'PNG (картинки слайдов)' },
								]}
								value={pres.gammaExportAs ?? 'pptx'}
								onChange={(v) => updatePres({ gammaExportAs: v as PresentationSettings['gammaExportAs'] })}
								disabled={isLoading}
							/>
							<SettingRowSelect
								label="Тон"
								hint="Стиль и интонация подачи материала. Формальный — для делового контекста, Дружелюбный — для маркетинга, Убедительный — для продаж, Обучающий — для лекций."
								hintAriaLabel={t('generate.settings.hint')}
								open={openKey === 'gammaTone'}
								onToggle={() => toggle('gammaTone')}
								options={[
									{ value: '', label: 'Авто (Gamma решит)' },
									{ value: 'formal', label: 'Формальный' },
									{ value: 'casual', label: 'Неформальный' },
									{ value: 'persuasive', label: 'Убедительный' },
									{ value: 'friendly', label: 'Дружелюбный' },
									{ value: 'inspirational', label: 'Вдохновляющий' },
									{ value: 'educational', label: 'Обучающий' },
									{ value: 'professional', label: 'Деловой' },
								]}
								value={pres.gammaTone ?? ''}
								onChange={(v) => updatePres({ gammaTone: v || undefined })}
								disabled={isLoading}
							/>
							<GammaHeaderFooterMobileRow
								pres={pres}
								onSave={(v) => updatePres(hfSettingsPatch(v))}
								disabled={isLoading}
							/>
							{/* Аудитория — короткий single-line input (1 строка достаточна:
							    «инвесторы B2B», «школьники 5-7 класс»). Используем тот же
							    .mss-row--textarea что и Instructions, но textarea height: 44px. */}
							<GammaAudienceMobileRow
								value={pres.gammaAudience || ''}
								onChange={(text) => updatePres({ gammaAudience: text || undefined })}
								disabled={isLoading}
							/>
							{/* AdditionalInstructions — inline textarea row (длинный текст
							    не вмещается в SettingRowSelect popup-list). */}
							<GammaInstructionsMobileRow
								value={pres.gammaAdditionalInstructions || ''}
								onChange={(text) =>
									updatePres({ gammaAdditionalInstructions: text || undefined })
								}
								disabled={isLoading}
							/>
						</>
					)}

					{/* Картинки — общий row для обоих режимов (загруженные пользователем
					    картинки которые AI вставит в слайды). */}
					{isPresentation && (
						<div className="mss-row mss-row--select">
							<button
								type="button"
								className="mss-row__header"
								onClick={() => updatePres({ _openImages: true })}
								disabled={isLoading}
							>
								<div className="mss-row__label-wrap">
									<span className="mss-row__label">Картинки</span>
								</div>
								<div className="mss-row__current">
									<span className="mss-row__current-val">
										{pres.imageUrls?.length ? `${pres.imageUrls.length}/5` : 'Нет'}
									</span>
									<span className="mss-row__chevron">
										<IconChevron />
									</span>
								</div>
							</button>
						</div>
					)}

					{/* ── HeyGen-specific controls ──────────────────────────
					    На desktop эти контролы живут в .gm__pills как pills
					    (Avatar/Voice picker'ы + BottomBarPopover'ы для style/
					    speed/emotion/caption/language/toggles). На мобилке
					    .gm__bottom скрыт, поэтому дублируем контролы здесь
					    через MSS-row компоненты + те же Sheet picker'ы что
					    и на desktop (только в Sheet bottom, без AnchoredPopover). */}
					{(() => {
						const heyKind = getHeyGenModelKind(model.backendModel)
						if (!heyKind) return null
						const speed = settings.heygenVoiceSpeed ?? HEYGEN_SPEED.default

						if (heyKind === 'video-agent') {
							return (
								<>
									<HeyGenAvatarMobileRow
										value={settings.heygenAvatarId}
										onChange={(id) => onSettingChange('heygenAvatarId', id || undefined)}
										allowAutoPick
										disabled={isLoading}
									/>
									<HeyGenVoiceMobileRow
										value={settings.heygenVoiceId}
										onChange={(id) => onSettingChange('heygenVoiceId', id || undefined)}
										allowAutoPick
										disabled={isLoading}
									/>
								</>
							)
						}

						if (heyKind === 'avatar-iv') {
							return (
								<>
									<HeyGenAvatarMobileRow
										value={settings.heygenAvatarId}
										onChange={(id) => onSettingChange('heygenAvatarId', id || undefined)}
										disabled={isLoading}
									/>
									<HeyGenVoiceMobileRow
										value={settings.heygenVoiceId}
										onChange={(id) => onSettingChange('heygenVoiceId', id || undefined)}
										disabled={isLoading}
									/>
									<SettingRowSelect
										label="Стиль аватара HeyGen"
										hint="Обрамление аватара HeyGen (normal / closeUp / circle)"
										hintAriaLabel={t('generate.settings.hint')}
										open={openKey === 'heygenStyle'}
										onToggle={() => toggle('heygenStyle')}
										options={HEYGEN_AVATAR_STYLES.map((s) => ({ value: s.value, label: s.label }))}
										value={settings.heygenAvatarStyle || 'normal'}
										onChange={(v) =>
											onSettingChange('heygenAvatarStyle', v as 'normal' | 'closeUp' | 'circle')
										}
										disabled={isLoading}
									/>
									<SettingRowSlider
										label={`Скорость голоса`}
										hint="Скорость TTS-голоса (0.5×–1.5×)"
										hintAriaLabel={t('generate.settings.hint')}
										value={speed}
										min={HEYGEN_SPEED.min}
										max={HEYGEN_SPEED.max}
										step={HEYGEN_SPEED.step}
										unit="×"
										onChange={(v) => onSettingChange('heygenVoiceSpeed', v)}
										disabled={isLoading}
									/>
									<SettingRowSelect
										label="Эмоция голоса"
										hint="Применяется только к голосам с поддержкой эмоций"
										hintAriaLabel={t('generate.settings.hint')}
										open={openKey === 'heygenEmotion'}
										onToggle={() => toggle('heygenEmotion')}
										options={[
											{ value: '', label: 'Без эмоции' },
											...HEYGEN_EMOTIONS.map((e) => ({ value: e, label: e })),
										]}
										value={settings.heygenVoiceEmotion || ''}
										onChange={(v) => onSettingChange('heygenVoiceEmotion', v || undefined)}
										disabled={isLoading}
									/>
									<SettingRowToggle
										label="Субтитры на видео"
										hint="Burn-in субтитры (нельзя убрать после генерации)"
										hintAriaLabel={t('generate.settings.hint')}
										value={!!settings.heygenCaption}
										onChange={(v) => onSettingChange('heygenCaption', v)}
										disabled={isLoading}
									/>
								</>
							)
						}

						if (heyKind === 'video-translate') {
							return <HeyGenTranslateMobileRows
								settings={settings}
								onSettingChange={onSettingChange}
								openKey={openKey}
								toggle={toggle}
								isLoading={isLoading}
							/>
						}

						return null
					})()}

					{/* Режим Seedance («Кадры | Референсы») — на телефоне его не было
					    вовсе, хотя от него зависит, как модель использует фото. */}
					{p.seedanceMode && (
						<SettingRowSelect
							label="Режим"
							hint="«Кадры» — фото становится первым кадром видео. «Референсы» — модель берёт с фото только облик, сцену рисует промпт."
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'seedanceMode'}
							onToggle={() => toggle('seedanceMode')}
							value={settings.seedanceMode ?? 'frames'}
							options={[
								{ value: 'frames', label: '🖼 Кадры' },
								{ value: 'references', label: '🎨 Референсы' },
							]}
							onChange={(v) => {
								const mode = v === 'references' ? 'references' : 'frames'
								// Смена режима чистит несовместимые загрузки — логика
								// одна с десктопом и приходит обработчиком сверху.
								if (onSeedanceModeChange) onSeedanceModeChange(mode)
								else onSettingChange('seedanceMode', mode)
							}}
							disabled={isLoading}
						/>
					)}

					{/* Намерение при видео-референсе Seedance 2.5 — те же варианты, что в пилюле. */}
					{p.videoIntent && (
						<SettingRowSelect
							label="Что сделать с видео"
							hint={VIDEO_INTENT_HINTS[videoIntent]}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'videoIntent'}
							onToggle={() => toggle('videoIntent')}
							value={videoIntent}
							options={VIDEO_INTENT_OPTIONS}
							onChange={(v) => {
								const intent: VideoIntent = v === 'new' ? 'new' : 'edit'
								if (onVideoIntentChange) onVideoIntentChange(intent)
								else onSettingChange('videoIntent', intent)
							}}
							disabled={isLoading}
						/>
					)}

					{trendNote && <SettingRowInfo label={trendNote.label} value={trendNote.value} />}

					{p.aspectRatio && !trendNote && (
						<SettingRowSelect
							label={hintLabel('aspectRatio', t('generate.settings.aspectRatio'))}
							hint={aspectLocked ? NOTE_ASPECT_FOLLOWS : hint('aspectRatio')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'aspectRatio'}
							onToggle={() => toggle('aspectRatio')}
							options={
								aspectLocked
									? [{ value: settings.aspectRatio, label: ASPECT_FOLLOWS_LABEL }]
									: aspectRatios.map((r: string) => ({
										value: r,
										label: r === 'match_input_image' ? t('generate.settings.matchInputImage') : r,
										icon: <AspectRatioIcon ratio={r} size={16} />,
									}))
							}
							value={settings.aspectRatio}
							onChange={(v) => onSettingChange('aspectRatio', v)}
							disabled={isLoading || aspectLocked}
						/>
					)}

					{/* Качество (gpt-image-2: low/medium/high) — было только в
					    десктопном ряду пилюль (SettingsPillsRow), в мобильном шите
					    отсутствовало → дрифт. Зеркалим тот же ключ/лейблы. */}
					{p.quality && (
						<SettingRowSelect
							label={hintLabel('quality', 'Качество')}
							hint={hint('quality')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'quality'}
							onToggle={() => toggle('quality')}
							options={qualityLevels.map((q: string) => ({
								value: q,
								label: qualityLabelFull(q),
							}))}
							value={settings.quality}
							onChange={(v) => onSettingChange('quality', v)}
							disabled={isLoading}
						/>
					)}

					{p.duration && !trendNote && (
						<SettingRowSelect
							label={hintLabel('duration', t('generate.settings.duration'))}
							hint={durLock ? durLock.note : hint('duration')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'duration'}
							onToggle={() => toggle('duration')}
							options={
								followSec != null
									? [{ value: String(followSec), label: `${followSec} ${t('generate.settings.sec')}` }]
									: durations.map((d: string) => ({ value: d, label: `${d} ${t('generate.settings.sec')}` }))
							}
							value={followSec != null ? String(followSec) : settings.duration}
							onChange={(v) => {
								onSettingChange('duration', v)
								if (model.provider === 'hailuo' && v === '10' && settings.resolution === '1080p')
									onSettingChange('resolution', '768p')
							}}
							disabled={isLoading || followSec != null}
						/>
					)}

					{/* «Повторить движение»: длину задают ручки отрезка в зоне ролика — второй ползунок разошёлся бы с ними. */}
					{(p.motionControl || p.videoReplace) && !motionTool && uploadedVideoDuration && uploadedVideoDuration > videoMin && (
						<SettingRowSlider
							label={hintLabel('duration', t('generate.settings.duration'))}
							hint={hint('duration')}
							hintAriaLabel={t('generate.settings.hint')}
							value={selectedVideoDuration}
							min={videoMin}
							max={uploadedVideoDuration}
							unit={' ' + t('generate.settings.sec')}
							onChange={(v) => {
								onVideoDurationChange(v)
								if (p.motionControl && v > 10 && settings.characterOrientation === 'image')
									onSettingChange('characterOrientation', 'video')
							}}
							disabled={isLoading}
						/>
					)}

					{p.durationSlider && !trendNote && (
						<SettingRowSlider
							label={hintLabel('duration', t('generate.settings.duration'))}
							hint={durLock ? durLock.note : hint('duration')}
							hintAriaLabel={t('generate.settings.hint')}
							value={followSec ?? Number(settings.duration)}
							min={Number(durations[0])}
							max={Number(durations[durations.length - 1])}
							unit={' ' + t('generate.settings.sec')}
							onChange={(v) => {
								onSettingChange('duration', String(v))
								if (model.provider === 'hailuo' && v > 6 && settings.resolution === '1080p')
									onSettingChange('resolution', '768p')
							}}
							disabled={isLoading || followSec != null}
						/>
					)}

					{p.resolution && (
						<SettingRowSelect
							label={hintLabel('resolution', t('generate.settings.resolution'))}
							hint={hint('resolution')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'resolution'}
							onToggle={() => toggle('resolution')}
							options={resolutions.map((r: string) => ({ value: r, label: r }))}
							value={draftOn ? DRAFT_RESOLUTION_LABEL : settings.resolution}
							onChange={(v) => {
								onSettingChange('resolution', v)
								if (model.provider === 'hailuo' && v === '1080p' && settings.duration === '10')
									onSettingChange('duration', '6')
							}}
							disabled={isLoading || draftOn}
						/>
					)}

					{showSound && (
						<SettingRowToggle
							label="Audio"
							hint={hint('sound')}
							hintAriaLabel={t('generate.settings.hint')}
							value={settings.sound}
							onChange={(v) => onSettingChange('sound', v)}
							disabled={isLoading}
						/>
					)}

					{/* Черновик Seedance 2.5: проба 480p, из которой делается финал 1080p. */}
					{p.draft && (
						<SettingRowToggle
							label="Черновик"
							hint={draftOn ? NOTE_DRAFT : DRAFT_HINT}
							hintAriaLabel={t('generate.settings.hint')}
							value={settings.draft === true}
							onChange={(v) => onSettingChange('draft', v)}
							disabled={isLoading}
						/>
					)}

					{/* Веб-поиск: на телефоне отсутствовал, хотя модель его умеет. */}
					{p.webSearch && (
						<SettingRowToggle
							label="Web"
							hint="Модель ищет в интернете свежие факты перед ответом."
							hintAriaLabel={t('generate.settings.hint')}
							value={!!settings.webSearch}
							onChange={(v) => onSettingChange('webSearch', v)}
							disabled={isLoading}
						/>
					)}

					{p.negativePrompt && (
						<SettingRowToggle
							label="Negative Prompt"
							hint={hint('negativePrompt')}
							hintAriaLabel={t('generate.settings.hint')}
							value={negativePromptOpen}
							onChange={onNegativePromptToggle}
							disabled={isLoading}
						/>
					)}

					{p.promptOptimizer && (
						<SettingRowToggle
							label="Prompt Accuracy"
							hint={hint('promptOptimizer')}
							hintAriaLabel={t('generate.settings.hint')}
							value={settings.promptOptimizer}
							onChange={(v) => onSettingChange('promptOptimizer', v)}
							disabled={isLoading}
						/>
					)}

					{p.midjourney && (
						<SettingRowSelect
							label={hintLabel('speed', t('generate.settings.speed'))}
							hint={hint('speed')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'speed'}
							onToggle={() => toggle('speed')}
							options={speedOptions}
							value={settings.speed}
							onChange={(v) => onSettingChange('speed', v as GenerateSettings['speed'])}
							disabled={isLoading}
						/>
					)}

					{p.midjourney && (
						<SettingRowSelect
							label={t('generate.settings.version')}
							open={openKey === 'version'}
							onToggle={() => toggle('version')}
							options={versionOptions}
							value={settings.version}
							onChange={(v) => onSettingChange('version', v)}
							disabled={isLoading}
						/>
					)}

					{p.midjourney && (
						<SettingRowSlider
							label={hintLabel('stylization', t('generate.settings.stylization'))}
							hint={hint('stylization')}
							hintAriaLabel={t('generate.settings.hint')}
							value={settings.stylization}
							min={0}
							max={1000}
							step={10}
							onChange={(v) => onSettingChange('stylization', v)}
							disabled={isLoading}
						/>
					)}

					{p.imagen && (
						<SettingRowSelect
							label={t('generate.settings.contentFilter')}
							open={openKey === 'safetyFilter'}
							onToggle={() => toggle('safetyFilter')}
							options={safetyOptions}
							value={settings.safetyFilter}
							onChange={(v) =>
								onSettingChange('safetyFilter', v as GenerateSettings['safetyFilter'])
							}
							disabled={isLoading}
						/>
					)}

					{p.outputFormat && (
						<SettingRowSelect
							label={t('generate.settings.format')}
							open={openKey === 'outputFormat'}
							onToggle={() => toggle('outputFormat')}
							options={formatOptions}
							value={effectiveOutputFormat(model, settings.outputFormat)}
							onChange={(v) =>
								onSettingChange('outputFormat', v as GenerateSettings['outputFormat'])
							}
							disabled={isLoading}
						/>
					)}

					{p.motionControl && (
						<SettingRowSelect
							label={hintLabel('characterOrientation', t('generate.settings.characterOrientation'))}
							hint={hint('characterOrientation')}
							hintAriaLabel={t('generate.settings.hint')}
							open={openKey === 'characterOrientation'}
							onToggle={() => toggle('characterOrientation')}
							options={orientationOptions}
							value={settings.characterOrientation}
							onChange={(v) => {
								onSettingChange(
									'characterOrientation',
									v as GenerateSettings['characterOrientation'],
								)
								// «По изображению» Kling берёт до 10 с — длиннее отказывал через минуты ожидания.
								const capped = motionDurationForOrientation(v, selectedVideoDuration)
								if (capped !== selectedVideoDuration) onVideoDurationChange(capped)
							}}
							disabled={isLoading}
						/>
					)}

					{(p.motionControl || p.videoReplace) && !motionTool && (
						<SettingRowToggle
							label={hintLabel('keepOriginalSound', t('generate.settings.keepSound'))}
							hint={hint('keepOriginalSound')}
							hintAriaLabel={t('generate.settings.hint')}
							value={settings.keepOriginalSound}
							onChange={(v) => onSettingChange('keepOriginalSound', v)}
							disabled={isLoading}
						/>
					)}

					{p.googleSearch && (
						<SettingRowToggle
							label="Google Search"
							value={settings.googleSearch}
							onChange={(v) => onSettingChange('googleSearch', v)}
							disabled={isLoading}
						/>
					)}
				</div>
			</div>

			{/* Nested шторка для audio-стиля. Рендерится поверх settings-sheet
			    через Sheet → createPortal (отдельный DOM-узел в body). */}
			{model.category === 'audio' && (
				<SunoStyleSheet
					open={styleSheetOpen}
					onClose={() => setStyleSheetOpen(false)}
					model={model}
					currentStyle={settings.audioStyle || ''}
					currentNegativeTags={settings.audioNegativeTags || ''}
					onApply={({ style, negativeTags }) => {
						onSettingChange('audioStyle', style)
						onSettingChange('audioNegativeTags', negativeTags)
					}}
				/>
			)}
		</div>,
		document.body,
	)
}
